import React, { useEffect, useState } from "react";
import { resolveMediaBlocks, resolveTextBlocks } from "./textLayout";
import { resolveBackground } from "./storyBackgrounds";
import { getCachedImage, preloadImage } from "./preloadStories";
import { resolveStoryFont } from "./storyFonts";
import { StorySticker } from "./storyStickers";

function MediaFrame({ block, playing, muted, videoRef, onVideoEnd }) {
  if (!block.url && !block.sticker_key) return null;
  const fit = block.fit === "contain" ? "contain" : "cover";
  const style = {
    objectFit: fit,
    objectPosition: `${block.pan_x ?? 50}% ${block.pan_y ?? 50}%`,
  };
  return (
    <div
      className={`absolute ${block.sticker_key || block.type === "gif" ? "overflow-visible" : "overflow-hidden"}`}
      style={{
        left: `${block.x}%`,
        top: `${block.y}%`,
        width: `${block.w}%`,
        height: `${block.h}%`,
        transform: `translate(-50%, -50%) rotate(${block.rotation || 0}deg)`,
      }}
    >
      {block.sticker_key ? (
        <StorySticker stickerKey={block.sticker_key} />
      ) : block.type === "video" ? (
        <video
          ref={videoRef}
          src={block.url}
          className="h-full w-full"
          style={style}
          playsInline
          preload="auto"
          autoPlay={playing}
          muted={muted}
          onEnded={onVideoEnd}
        />
      ) : (
        <img src={block.url} alt="" className="h-full w-full" style={style} />
      )}
    </div>
  );
}

export default function UpdateSlide({
  update,
  videoRef,
  onVideoEnd,
  playing = true,
  hideText = false,
  hideMedia = false,
  onReady,
  onMediaSize,
  muted = false,
}) {
  const mediaBlocks = resolveMediaBlocks(update);
  const blocks = resolveTextBlocks(update);
  const background = resolveBackground(update);
  const firstImage = mediaBlocks.find((b) => b.type === "image" && b.url);
  const firstVideo = mediaBlocks.find((b) => b.type === "video" && b.url);
  const [imageReady, setImageReady] = useState(() => !firstImage || !!getCachedImage(firstImage.url));

  useEffect(() => {
    if (!firstImage?.url) {
      setImageReady(true);
      onReady?.();
      return undefined;
    }
    if (getCachedImage(firstImage.url)) {
      setImageReady(true);
      onReady?.();
      return undefined;
    }
    setImageReady(false);
    let cancelled = false;
    preloadImage(firstImage.url).then((img) => {
      if (cancelled) return;
      setImageReady(true);
      onReady?.();
      if (img?.naturalWidth) onMediaSize?.({ width: img.naturalWidth, height: img.naturalHeight });
    });
    return () => {
      cancelled = true;
    };
  }, [firstImage?.url, onReady, onMediaSize]);

  useEffect(() => {
    if (firstVideo?.url) onReady?.();
  }, [firstVideo?.url, onReady]);

  let videoAssigned = false;

  return (
    <div className="relative h-full w-full overflow-hidden bg-slate-950">
      <div className="absolute inset-0" style={background} />

      {!hideMedia && imageReady
        ? mediaBlocks.map((block) => {
            const attachRef = block.type === "video" && block.url && !videoAssigned;
            if (attachRef) videoAssigned = true;
            return (
              <MediaFrame
                key={block.id}
                block={block}
                playing={playing}
                muted={muted}
                videoRef={attachRef ? videoRef : undefined}
                onVideoEnd={onVideoEnd}
              />
            );
          })
        : null}

      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none" />

      {!hideText
        ? blocks.map((block) => {
            const text = String(block.text || "").trim();
            if (!text) return null;
            return (
              <div
                key={block.id}
                className="absolute max-w-[80%] text-center font-bold leading-snug whitespace-pre-wrap drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)] pointer-events-none"
                style={{
                  left: `${block.x}%`,
                  top: `${block.y}%`,
                  color: block.color || "#ffffff",
                  fontFamily: resolveStoryFont(block.font),
                  fontSize: 22,
                  transform: `translate(-50%, -50%) rotate(${block.rotation || 0}deg) scale(${block.scale})`,
                  transformOrigin: "center center",
                }}
              >
                {text}
              </div>
            );
          })
        : null}
    </div>
  );
}
